import React, { useEffect } from 'react';
import { updateSocialMeta } from '../utils/seo';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Gift } from 'lucide-react';

const GiftCards = () => {
  useEffect(() => {
    document.title = 'Gift Cards - Pin High Golf & Lounge | Give the Gift of Golf in Bay Village';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Purchase gift cards for Pin High Golf & Lounge. Perfect gift for golfers - simulator time, lessons, memberships, and more. Available in any amount.');
    }

    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://pinhighbay.com/gift-cards');
    updateSocialMeta({
      title: 'Gift Cards - Pin High Golf & Lounge | Give the Gift of Golf in Bay Village',
      description: 'Purchase gift cards for Pin High Golf & Lounge. Perfect gift for golfers - simulator time, lessons, memberships, and more. Available in any amount.',
      path: '/gift-cards',
    });
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-green-600 to-green-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Gift className="w-20 h-20 mx-auto mb-6 text-green-300" />
          <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
            Gift Cards
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto text-green-50">
            Give the gift of golf. Perfect for birthdays, holidays, or any occasion.
          </p>
        </div>
      </section>

      {/* Gift Card Info */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-none shadow-xl">
            <CardContent className="p-12 text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
                Purchase a Gift Card
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Gift cards can be used for simulator time, lessons, memberships, food and beverages, and merchandise. Available in any amount.
              </p>
              <Button 
                asChild
                size="lg" 
                className="bg-green-600 hover:bg-green-700 text-white text-lg px-8 py-6 transition-all duration-300"
              >
                <a href="https://order.toasttab.com/egiftcards/pin-high-27229-wolf-road" target="_blank" rel="noopener noreferrer">
                  Buy Gift Card
                </a>
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              Why Give a Pin High Gift Card?
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-none shadow-lg text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Gift className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">Perfect for Any Occasion</h3>
                <p className="text-gray-600">
                  Birthdays, holidays, thank you gifts, or just because. A gift card to Pin High is always appreciated.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Gift className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">Flexible Use</h3>
                <p className="text-gray-600">
                  Use your gift card for simulator time, lessons, memberships, food, drinks, or merchandise. The choice is theirs.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Gift className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">Any Amount</h3>
                <p className="text-gray-600">
                  Choose any amount that fits your budget. Gift cards never expire and can be used anytime.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GiftCards;
